import { AlertTriangle, CalendarClock, ShieldCheck } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
  Card, CardContent,
} from "@opencloud/ui";
import { agentBrand } from "../brand.js";

const uuid = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const completedRepair = new RegExp(`^OpenCloud's app agent completed repair (${uuid})\\. Evidence: ([\\s\\S]*)\\.$`, "i");
const assignmentMarker = "This is an independent scheduled task. Complete only these assignments; do not resume the main conversation or spawn more agents.";
const detectedIncident = new RegExp(`^OpenCloud detected a trusted app-scoped incident\\.\\nIncident: (${uuid})\\n`, "i");

// Compatibility presentation for retained platform system messages. Never
// classify user/model prose, or infer current deployment truth from this text.
// The stored wording is a fixed format (the patterns above match it whatever
// the brand); people see the branded summaries below instead of the raw text.
export function automationMessage(content: string) {
  if (detectedIncident.test(content.trim())) {
    const problem = /^Problem: (.+)$/m.exec(content);
    return {
      kind: "incident" as const,
      title: problem?.[1]?.trim() || "App problem detected",
      summary: `${agentBrand().productName} detected a problem in the app and requested an automatic repair.`,
      instructions: content.trim().split("\n").slice(1).join("\n"),
    };
  }
  const repair = completedRepair.exec(content.trim());
  if (repair) return {
    kind: "repair" as const,
    title: "Automatic repair completed",
    summary: "The app agent completed this repair and recorded its recovery evidence.",
    evidence: [
      { label: "Repair", value: repair[1]! },
      ...repair[2]!.split(", ").filter(Boolean).map(item => {
        const separator = item.indexOf(":");
        const kind = separator < 0 ? "" : item.slice(0, separator);
        const labels: Record<string, string> = { run: "Agent run", deployment: "Release", artifact: "Build artifact", verification: "Verification" };
        return { label: labels[kind] ?? "Evidence", value: separator < 0 ? item : item.slice(separator + 1) };
      }),
    ],
  };
  const paragraphs = content.trim().split(/\n\s*\n/);
  if (paragraphs[0]?.startsWith("Assignment: ") && paragraphs[1] === assignmentMarker) {
    return {
      kind: "assignment" as const,
      title: paragraphs[0].slice("Assignment: ".length).trim() || "Scheduled assignment",
      summary: "The app requested an independent agent task. Its instructions and scope are recorded below.",
      instructions: paragraphs.slice(1).join("\n\n"),
    };
  }
  return null;
}

export function AutomationMessageCard({ event, messageId, focused }: {
  event: NonNullable<ReturnType<typeof automationMessage>>;
  messageId: string;
  focused: boolean;
}) {
  const repair = event.kind === "repair";
  const incident = event.kind === "incident";
  const Icon = repair ? ShieldCheck : incident ? AlertTriangle : CalendarClock;
  return <Card className="a3-automation-message" id={`message-${messageId}`}
    data-message-id={messageId} data-search-focus={focused || undefined}
    data-automation-kind={event.kind} aria-label={event.title}>
    <CardContent>
      <div className="a3-automation-message-heading">
        <span className="a3-automation-message-icon"><Icon aria-hidden="true" /></span>
        <div><p className="a3-eyebrow">{repair ? "Automatic repair" : incident ? "Incident detected" : "Scheduled task"}</p>
          <h3>{event.title}</h3></div>
      </div>
      <p className="a3-automation-message-summary">{event.summary}</p>
      <Accordion>
        <AccordionItem value="details">
          <AccordionTrigger>{repair ? "Repair evidence" : incident ? "Incident details" : "Assignment instructions"}</AccordionTrigger>
          <AccordionContent>
            {event.kind === "repair" ? <dl className="a3-automation-message-evidence">
              {event.evidence.map((item, index) => <div key={index}><dt>{item.label}</dt><dd><code>{item.value}</code></dd></div>)}
            </dl> : <div className="a3-automation-message-instructions">{event.instructions}</div>}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </CardContent>
  </Card>;
}
